import { Bank, CaretDown, CaretRight } from 'phosphor-react';
import type { BankTransactionRecord } from '@dill/dill-shared';
import { formatCurrency, formatDate } from '../utils';

/**
 * The payments a single bank credit brought in, shown as one thing.
 *
 * A cheque file banked in bulk arrives as one credit backing several advices, and the ERP deposit
 * that reconciles it is one document covering all of them. So they cannot be worked one at a time:
 * approving them is a single decision, and the deposit's outcome is a single answer. A band says
 * that — these rows belong together, this total is what the bank credited, and the action at the
 * top applies to all of it.
 *
 * A credit with one payment gets a band too, in 1-Click Review. Every record in that queue is
 * settled, so every one of them has a credit, and giving the single-payment case a different shape
 * would only mean two layouts to read instead of one — the band still says which credit the payment
 * arrived in and whether it accounts for all of it.
 *
 * Presentational only. The member rows are the caller's, because 1-Click Review and Post History
 * show different columns for a payment; what is shared is the frame and the header, so the two
 * screens cannot drift on how a bulk deposit is described.
 */
interface DepositGroupBandProps {
  /** The credit itself, for its amount and date. Null when it has not loaded or does not exist. */
  deposit: BankTransactionRecord | null;
  /**
   * Overrides the credit-derived heading, for the one case where there is no credit to describe:
   * a record settled before `settlementBankRecordId` was stored.
   */
  title?: string;
  /** The ERP's deposit document, once one exists. */
  erpDepositId?: string | null;
  paymentCount: number;
  paymentTotal: number;
  /**
   * What the credit is still owed, from `depositUnaccountedAmount`.
   *
   * Shown rather than merely disabling the action, because "off by $7,615.18" is what sends someone
   * to the Unmatched Deposits queue; a greyed-out button with no figure sends them nowhere.
   */
  unaccounted?: number;
  /** The group's deposit status, where the screen has one to show. */
  status?: React.ReactNode;
  /** Approve group, Retry deposit, or nothing. */
  action?: React.ReactNode;
  /**
   * Collapsing is offered, not imposed: bands open by default, because a queue that hides its own
   * contents behind a row of carets makes the operator click before they can see whether there is
   * anything to do.
   *
   * Omit `onToggle` and the band has no caret and cannot close.
   */
  expanded?: boolean;
  onToggle?: () => void;
  children: React.ReactNode;
}

export const DepositGroupBand = ({
  deposit,
  title,
  erpDepositId,
  paymentCount,
  paymentTotal,
  unaccounted = 0,
  status,
  action,
  expanded = true,
  onToggle,
  children,
}: DepositGroupBandProps) => {
  const isShort = unaccounted > 0.005;

  const heading = (
    <>
      {onToggle &&
        (expanded ? (
          <CaretDown size={12} weight="bold" className="text-gray-400 shrink-0" />
        ) : (
          <CaretRight size={12} weight="bold" className="text-gray-400 shrink-0" />
        ))}
      <Bank size={15} weight="fill" className={isShort ? 'text-amber-600' : 'text-blue-600'} />
      <div className="min-w-0">
        <p className="text-xs font-semibold text-gray-900 truncate">
          {title ?? (deposit ? `${formatCurrency(deposit.amount)} bank deposit` : 'Bank deposit')}
          {deposit?.date && (
            <span className="ml-1.5 font-normal text-gray-500">
              &middot; {formatDate(deposit.date)}
            </span>
          )}
        </p>
        <p className="text-[11px] text-gray-500 truncate">
          {paymentCount} payment{paymentCount === 1 ? '' : 's'} &middot;{' '}
          {formatCurrency(paymentTotal)}
          {isShort && (
            <span className="text-amber-700">
              {' '}
              &middot; {formatCurrency(unaccounted)} unaccounted
            </span>
          )}
          {/* Mono, and only once it exists — this is the id someone looks up in QuickBooks. */}
          {erpDepositId && <span className="ml-1.5 font-mono text-gray-400">{erpDepositId}</span>}
        </p>
      </div>
    </>
  );

  return (
    <div
      className={
        'rounded-lg border bg-white overflow-hidden ' +
        (isShort ? 'border-amber-200' : 'border-gray-200')
      }>
      <div
        className={
          'flex items-center justify-between gap-3 px-3 py-2 ' +
          (expanded ? 'border-b ' : '') +
          (isShort ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-blue-50/60')
        }>
        {/*
          Only the heading is the toggle, never the whole header — the action lives in here too, and
          a button inside a button is invalid markup that swallows the inner click.
        */}
        {onToggle ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            className="flex items-center min-w-0 gap-2 text-left bg-transparent">
            {heading}
          </button>
        ) : (
          <div className="flex items-center min-w-0 gap-2">{heading}</div>
        )}
        <div className="flex items-center gap-2 shrink-0">
          {status}
          {action}
        </div>
      </div>
      {expanded && <div className="flex flex-col divide-y divide-gray-100">{children}</div>}
    </div>
  );
};
